/**
 * Outcomes Monitoring Report — the unified conduct/detriment monitoring layout.
 *
 * The framework (Consumer Duty vs TCF) is FIXED per report/route — there is no
 * in-page framework toggle. Consumer Duty and TCF
 * remain separate reports; the same component is mounted per framework.
 *
 * Level 1 (overview) and Level 2 (outcome detail) are one page; the level is
 * driven by the `outcome` URL param, alongside `grain` / `plot` / `bucket` so a
 * scoped drill-down is shareable (SPEC §7).
 */

import { useCallback, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Clock, Loader2 } from 'lucide-react';

import { ErrorBoundary } from '../../../ui/error-boundary';
import { Skeleton } from '../../../ui/skeleton';
import { Timestamp } from '../../../ui/timestamp';
import { FilterStrip } from '../../records/filter';
import { useReportFilterState } from '../useReportFilterState';
import { useDatasets } from '../../../../hooks/useDatasets';
import { useCollection } from '../../../../hooks/useRecords';
import { AggregateRow, DetailView, OutcomeCard, OutcomesExportButton } from './components';
import { FRAMEWORK_CONFIG } from './constants';
import { useMonitoringOverview } from './hooks';
import type { DetrimentType, Framework, Grain, Plot } from './types';
import { buildDetrimentDrilldownHref, grainOptionsFor, periodDays, periodLabel } from './utils';

const VALID_PLOTS: Plot[] = ['score', 'detriment'];

/** Stable across renders, so the filter hook's effect keeps its identity. */
const CLEAR_ON_FILTER_CHANGE = { clearParamsOnChange: ['bucket'] as const };

function OutcomesMonitoringReportInner({ framework }: { framework: Framework }) {
  const { datasetId } = useParams<{ datasetId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // `bucket` is a drill-down into the current filter set, so a filter change
  // invalidates it (SPEC §7); the filter hook clears it in the same rewrite that
  // writes the new filters.
  const { filters, setFilters, reset } = useReportFilterState(datasetId ?? '', CLEAR_ON_FILTER_CHANGE);

  // Each detriment figure drills into the records queue filtered to that
  // dimension, carrying the report's EFFECTIVE window — the injected 30-day
  // default included — so the records cohort matches the figure on screen.
  // Drilling from a 30-day view is an implicit 30-day scope, so the records
  // page persisting it is the correct landing, not a choice nobody made.
  const detrimentDrilldownHref = useCallback(
    (type: DetrimentType) =>
      buildDetrimentDrilldownHref(datasetId ?? '', type, filters.dateRange),
    [datasetId, filters.dateRange],
  );
  // The records queue applies a date range only as a known preset or a COMPLETE
  // custom range; any other bound-carrying shape (a custom range missing a bound,
  // or bounds with no/legacy preset) it drops, opening an all-time queue that no
  // longer matches the report. Suppress the drill-down whenever bounds are present
  // but not a complete custom range.
  const dr = filters.dateRange;
  const dateRangeUnmappable =
    // any custom range that isn't complete (missing a bound, or no bounds at all)
    (dr?.preset === 'custom' && !(dr.from && dr.to)) ||
    // bounds carried without a custom preset (preset-less / legacy URL state)
    (dr?.preset !== 'custom' && !!(dr?.from || dr?.to));
  // When a dimension filter is active the mix is scoped to that sub-cohort, so a
  // figure represents records carrying the clicked type WITHIN it — which the flat
  // records filter can't reconstruct. Suppress rather than open a broader queue.
  const dimensionsFiltered = (filters.dimensions?.values?.length ?? 0) > 0;
  const { data: datasetsData } = useDatasets();
  const currentDataset = useMemo(
    () => datasetsData?.datasets.find((d) => d.id === datasetId),
    [datasetsData, datasetId],
  );

  const config = FRAMEWORK_CONFIG[framework];
  const { data: collectionData } = useCollection(datasetId ?? '');
  const { data, isLoading, isFetching, isError } = useMonitoringOverview(datasetId ?? '', framework, filters);

  // --- URL-driven Level-2 state ---------------------------------------------
  const grainOptions = useMemo(() => grainOptionsFor(periodDays(filters)), [filters]);
  const outcomeId = searchParams.get('outcome');
  const urlGrain = searchParams.get('grain') as Grain | null;
  const grain: Grain =
    urlGrain && grainOptions.allowed.includes(urlGrain) ? urlGrain : grainOptions.default;
  const urlPlot = searchParams.get('plot') as Plot | null;
  const plot: Plot = urlPlot && VALID_PLOTS.includes(urlPlot) ? urlPlot : 'score';
  const selectedBucket = searchParams.get('bucket');

  /** Update our own URL params while preserving filter/pagination params. */
  const setParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === null) next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const openOutcome = useCallback(
    (id: string) => {
      // Change outcome resets bucket, plot, grain (SPEC §7); the open band is
      // local to BandTiles and remounts with the outcome.
      setParams({ outcome: id, grain: null, plot: null, bucket: null });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setParams],
  );
  const back = useCallback(
    () => setParams({ outcome: null, grain: null, plot: null, bucket: null }),
    [setParams],
  );
  const onGrainChange = useCallback(
    (g: Grain) => setParams({ grain: g, bucket: null }), // grain change clears selection
    [setParams],
  );
  const onPlotChange = useCallback((p: Plot) => setParams({ plot: p }), [setParams]);
  const onSelectBucket = useCallback(
    (b: string | null) => setParams({ bucket: b }),
    [setParams],
  );

  const maxBand = useMemo(
    () => (data ? Math.max(1, ...data.outcomes.flatMap((o) => [o.bands.low, o.bands.mod, o.bands.high])) : 1),
    [data],
  );
  const sortedOutcomes = useMemo(
    () => (data ? [...data.outcomes].sort((a, b) => b.score - a.score) : []),
    [data],
  );

  if (!datasetId) return <div>Dataset ID is required</div>;

  const skeletonCount = framework === 'tcf' ? 6 : 5;

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-[var(--go-spring)]">
      <div className="min-h-0 flex-1 overflow-auto px-4 py-4 md:px-6 md:py-5 xl:px-8 xl:py-6">
        <div className="flex w-full flex-col gap-6">
          {/* breadcrumb */}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Link to="/v4/records" className="font-medium text-primary hover:underline">
              Monitor
            </Link>
            <span>/</span>
            <Link to={`/v4/records/${datasetId}`} className="font-medium text-primary hover:underline">
              {currentDataset?.name ?? 'Collection'}
            </Link>
            <span>/</span>
            <span>{config.title}</span>
          </div>

          {/* header */}
          <section className="rounded-[28px] border border-border/70 bg-background/90 px-6 py-5 shadow-[var(--shadow-card)] backdrop-blur-sm">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  {config.eyebrow}
                </p>
                <h1 className="mt-2 font-jakarta text-3xl font-extrabold tracking-[-0.04em] text-foreground">
                  {config.title}
                </h1>
              </div>
              <OutcomesExportButton
                datasetId={datasetId}
                framework={framework}
                filters={filters}
                disabled={isLoading || isError}
                className="mt-1 shrink-0"
              />
            </div>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{config.standfirst}</p>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              {currentDataset?.lastActivity && (
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  Last updated <Timestamp value={currentDataset.lastActivity} format="absolute" />
                </span>
              )}
              <span className="rounded-full border border-border px-2.5 py-0.5 font-bold">
                {config.jurisdiction}
              </span>
            </div>
          </section>

          {/* filter bar — framework is NOT a filter, so it stays out of this strip */}
          <FilterStrip
            filters={filters}
            onFiltersChange={setFilters}
            onReset={reset}
            isLoading={isLoading}
            tcfAvailable={collectionData?.hasTcfData}
            className="rounded-[24px] border border-border/70 bg-background/90 p-4 shadow-[var(--shadow-card)] backdrop-blur-sm"
          />

          {isError ? (
            <div className="rounded-[var(--radius)] border border-border bg-background p-8 text-center text-destructive">
              Failed to load report data. Please try again.
            </div>
          ) : outcomeId ? (
            <DetailView
              datasetId={datasetId}
              framework={framework}
              filters={filters}
              outcomeId={outcomeId}
              grain={grain}
              allowedGrains={grainOptions.allowed}
              plot={plot}
              selectedBucket={selectedBucket}
              periodLabel={periodLabel(filters)}
              onGrainChange={onGrainChange}
              onPlotChange={onPlotChange}
              onSelectBucket={onSelectBucket}
              onBack={back}
            />
          ) : isLoading || !data ? (
            <div className="flex flex-col gap-6">
              <Skeleton className="h-32 w-full rounded-[var(--radius)]" />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: skeletonCount }).map((_, i) => (
                  <Skeleton key={i} className="h-[22rem] w-full rounded-[var(--radius)]" />
                ))}
              </div>
            </div>
          ) : (
            // Cached data is on screen but a background refetch is running (a
            // filter change): dim the figures and show a "Refreshing" pill so the
            // user knows the numbers are updating, instead of stale figures
            // sitting there with no feedback.
            <div className="relative">
              {isFetching && (
                <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center">
                  <div className="flex items-center gap-2 rounded-full border border-border bg-background/95 px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-[var(--shadow-card)]">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Refreshing…
                  </div>
                </div>
              )}
              <div
                className={`flex flex-col gap-6 transition-opacity ${
                  isFetching ? 'opacity-50' : 'opacity-100'
                }`}
                aria-busy={isFetching}
              >
                <AggregateRow
                  aggregate={data.aggregate}
                  // Suppress the drill-down (figures render static) when it can't
                  // map faithfully: during a refetch (shown figures are the previous
                  // window's), a half-entered custom range, or an active dimension
                  // filter (the mix is a sub-cohort the records filter can't rebuild).
                  detrimentDrilldownHref={
                    isFetching || dateRangeUnmappable || dimensionsFiltered
                      ? undefined
                      : detrimentDrilldownHref
                  }
                />

                <div>
                  <h2 className="font-jakarta text-xl font-bold tracking-[-0.02em] text-foreground">
                    {config.outcomesHeading}
                  </h2>
                </div>

                {sortedOutcomes.length ? (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {sortedOutcomes.map((o) => (
                      <OutcomeCard key={o.id} outcome={o} maxBand={maxBand} onOpen={openOutcome} />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-[var(--radius)] border border-border bg-background p-8 text-center text-muted-foreground">
                    No outcome data found for the selected filters
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function OutcomesMonitoringReportPage({ framework = 'cd' }: { framework?: Framework }) {
  return (
    <ErrorBoundary>
      <OutcomesMonitoringReportInner framework={framework} />
    </ErrorBoundary>
  );
}
